import { useEffect, useCallback, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { mediaUrl } from "@/lib/api";

/**
 * Full-screen lightbox with keyboard nav, swipe on mobile.
 * Props: images (string[]), open, initial index, onClose
 */
export default function Lightbox({ images = [], open, index = 0, onClose }) {
  const [i, setI] = useState(index);
  const [touchStart, setTouchStart] = useState(null);

  useEffect(() => setI(index), [index, open]);

  const next = useCallback(
    () => setI((v) => (v + 1) % images.length),
    [images.length]
  );
  const prev = useCallback(
    () => setI((v) => (v - 1 + images.length) % images.length),
    [images.length]
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, next, prev, onClose]);

  const onTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchStart == null) return;
    const delta = e.changedTouches[0].clientX - touchStart;
    if (Math.abs(delta) > 40) {
      if (delta < 0) next();
      else prev();
    }
    setTouchStart(null);
  };

  const current = images[i];

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[80] bg-ink/98 backdrop-blur-sm text-ivory"
          data-testid="lightbox"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div className="absolute top-4 left-6 md:top-6 md:left-12 label-mono opacity-70" data-testid="lightbox-counter">
            {i + 1} / {images.length}
          </div>
          <button
            type="button"
            className="absolute top-4 right-6 md:top-6 md:right-12 text-ivory"
            aria-label="Close"
            onClick={onClose}
            data-testid="lightbox-close"
          >
            <X size={26} />
          </button>

          <button
            type="button"
            aria-label="Previous"
            onClick={prev}
            className="absolute left-4 md:left-10 top-1/2 -translate-y-1/2 text-ivory/70 hover:text-ivory transition-colors"
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={40} />
          </button>
          <button
            type="button"
            aria-label="Next"
            onClick={next}
            className="absolute right-4 md:right-10 top-1/2 -translate-y-1/2 text-ivory/70 hover:text-ivory transition-colors"
            data-testid="lightbox-next"
          >
            <ChevronRight size={40} />
          </button>

          <div className="w-full h-full flex items-center justify-center p-16 md:p-20">
            <AnimatePresence mode="wait">
              <motion.img
                key={i}
                src={mediaUrl(current)}
                alt={`Image ${i + 1}`}
                className="max-w-full max-h-full object-contain"
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4, ease: [0.19, 1, 0.22, 1] }}
                data-testid="lightbox-image"
              />
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
